import { ref, unref } from 'vue';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { parseFaqLearningSourceGroups } from './useAgentConfigurationForm';

export function useAgentSaveOperations({
  assistantId,
  assistant,
  assistants,
  currentConfig,
  activeSection,
  identityForm,
  hasUnsavedChanges,
  faqLearningManagement,
  faqGroupListToText,
  validateIdentity,
  validateFaqLearning,
  buildIdentityConfig,
  buildBehaviorRules,
  buildKnowledgeConfig,
  buildHandoffConfig,
  markSaved,
  store,
  route,
  router,
  notify,
  track,
  t,
}) {
  const showDeleteAssistantModal = ref(false);
  const isDuplicatingAssistant = ref(false);
  const isDeletingAssistant = ref(false);
  const isSavingIdentity = ref(false);
  const isSavingBehavior = ref(false);
  const isSavingKnowledge = ref(false);
  const isSavingFaqLearning = ref(false);
  const isSavingHandoff = ref(false);

  const selectAssistant = assistantRecord => {
    if (!assistantRecord?.id || assistantRecord.id === unref(assistantId)) {
      return;
    }
    router.push({
      name: 'captain_assistants_experience_index',
      params: {
        accountId: route.params.accountId,
        assistantId: assistantRecord.id,
      },
      query: { section: unref(activeSection) },
    });
  };

  const handleAssistantCreated = newAssistant => {
    if (!newAssistant?.id) return;
    track(ACCOUNT_EVENTS.CAPTAIN_AGENT_CREATED, {
      assistantId: newAssistant.id,
    });
    router.push({
      name: 'captain_assistants_experience_index',
      params: {
        accountId: route.params.accountId,
        assistantId: newAssistant.id,
      },
      query: { section: 'identity' },
    });
  };

  const duplicateAssistant = async () => {
    const currentAssistant = unref(assistant);
    if (!currentAssistant?.id) return;
    if (unref(hasUnsavedChanges)) {
      notify(t('CAPTAIN_EXPERIENCE.WORKSPACE.SAVE_BEFORE_DUPLICATING'));
      return;
    }

    isDuplicatingAssistant.value = true;
    try {
      const newAssistant = await store.dispatch('captainAssistants/create', {
        name: t('CAPTAIN_EXPERIENCE.SCRIPT.DUPLICATED_ASSISTANT_NAME', {
          name: currentAssistant.name,
        }),
        description: currentAssistant.description,
        response_guidelines: currentAssistant.response_guidelines || [],
        guardrails: currentAssistant.guardrails || [],
        config: {
          ...unref(currentConfig),
          publication_mode: 'draft',
          lead_rollout_percent: '0',
        },
      });
      await store.dispatch('captainAssistants/get');
      notify(
        t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_ASSISTANT_SETTINGS_DUPLICATED')
      );
      selectAssistant(newAssistant);
    } catch (error) {
      notify(
        error?.message ||
          t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_DUPLICATE_ASSISTANT_ERROR')
      );
    } finally {
      isDuplicatingAssistant.value = false;
    }
  };

  const requestDeleteAssistant = () => {
    if (!unref(assistant)?.id) return;
    showDeleteAssistantModal.value = true;
  };

  const deleteAssistant = async () => {
    if (!unref(assistant)?.id) return;
    const currentAssistantId = unref(assistantId);
    const nextAssistant = unref(assistants).find(
      assistantRecord => assistantRecord.id !== currentAssistantId
    );

    isDeletingAssistant.value = true;
    try {
      await store.dispatch('captainAssistants/delete', currentAssistantId);
      showDeleteAssistantModal.value = false;
      notify(t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_ASSISTANT_DELETED'));
      if (nextAssistant) {
        selectAssistant(nextAssistant);
      } else {
        router.push({
          name: 'captain_assistants_create_index',
          params: { accountId: route.params.accountId },
        });
      }
    } catch (error) {
      notify(
        error?.message ||
          t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_DELETE_ASSISTANT_ERROR')
      );
    } finally {
      isDeletingAssistant.value = false;
    }
  };

  const saveAssistantSection = async ({
    successMessage,
    config,
    assistantAttributes = {},
    includeAssistantDetails = false,
    targetAssistantId = unref(assistantId),
  }) => {
    const assistantPayload = {
      id: targetAssistantId,
      ...assistantAttributes,
    };
    if (config) assistantPayload.config = config;
    if (includeAssistantDetails) {
      assistantPayload.name = identityForm.name;
      assistantPayload.description = identityForm.description;
    }

    await store.dispatch('captainAssistants/update', assistantPayload);
    if (targetAssistantId !== unref(assistantId)) return false;
    if (successMessage) notify(successMessage);
    return true;
  };

  const saveIdentity = async () => {
    if (!validateIdentity()) return;
    identityForm.name = identityForm.name.trim();
    identityForm.productName = identityForm.productName.trim();

    const targetAssistantId = unref(assistantId);
    isSavingIdentity.value = true;
    try {
      const saved = await saveAssistantSection({
        successMessage: t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_ASSISTANT_UPDATED'),
        config: buildIdentityConfig(),
        includeAssistantDetails: true,
        targetAssistantId,
      });
      if (!saved) return;
      markSaved(['identity']);
      track(ACCOUNT_EVENTS.CAPTAIN_AGENT_IDENTITY_SAVED, {
        assistantId: targetAssistantId,
        language: identityForm.language,
        featureCitation: identityForm.featureCitation,
      });
    } catch (error) {
      notify(
        error?.message ||
          t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_SAVE_ASSISTANT_ERROR')
      );
    } finally {
      isSavingIdentity.value = false;
    }
  };

  const saveBehavior = async () => {
    const targetAssistantId = unref(assistantId);
    const behaviorRules = buildBehaviorRules();
    isSavingBehavior.value = true;
    try {
      const saved = await saveAssistantSection({
        successMessage: t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_BEHAVIOR_SAVED'),
        assistantAttributes: behaviorRules,
        targetAssistantId,
      });
      if (!saved) return;
      markSaved(['behavior']);
      track(ACCOUNT_EVENTS.CAPTAIN_AGENT_BEHAVIOR_SAVED, {
        assistantId: targetAssistantId,
        responseGuidelinesCount: behaviorRules.response_guidelines.length,
        guardrailsCount: behaviorRules.guardrails.length,
      });
    } catch (error) {
      notify(
        error?.message ||
          t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_SAVE_BEHAVIOR_ERROR')
      );
    } finally {
      isSavingBehavior.value = false;
    }
  };

  const saveKnowledgeSettings = async () => {
    const targetAssistantId = unref(assistantId);
    isSavingKnowledge.value = true;
    try {
      const saved = await saveAssistantSection({
        successMessage: t(
          'CAPTAIN_EXPERIENCE.SCRIPT.ALERT_KNOWLEDGE_SETTINGS_SAVED'
        ),
        config: buildKnowledgeConfig(),
        targetAssistantId,
      });
      if (!saved) return;
      markSaved(['knowledge']);
      track(ACCOUNT_EVENTS.CAPTAIN_AGENT_KNOWLEDGE_SETTINGS_SAVED, {
        assistantId: targetAssistantId,
        approvedResponsesEnabled: identityForm.featureFaq,
        memoriesEnabled: identityForm.featureMemory,
      });
    } catch (error) {
      notify(
        error?.message ||
          t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_SAVE_KNOWLEDGE_SETTINGS_ERROR')
      );
    } finally {
      isSavingKnowledge.value = false;
    }
  };

  const saveFaqLearningSettings = async () => {
    if (faqLearningManagement.value.locked || !validateFaqLearning()) return;

    const targetAssistantId = unref(assistantId);
    const sourceGroups = parseFaqLearningSourceGroups(
      identityForm.faqLearningSourceGroupsText
    );
    const questionsGroupJid = identityForm.faqLearningQuestionsGroupJid.trim();
    isSavingFaqLearning.value = true;
    try {
      await store.dispatch('captainAssistants/update', {
        id: targetAssistantId,
        config: {
          faq_learning_source_groups: sourceGroups,
          faq_learning_questions_group_jid: questionsGroupJid,
        },
      });
      if (targetAssistantId !== unref(assistantId)) return;

      identityForm.faqLearningSourceGroupsText =
        faqGroupListToText(sourceGroups);
      identityForm.faqLearningQuestionsGroupJid = questionsGroupJid;
      markSaved(['faq']);
      notify(t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_FAQ_LEARNING_UPDATED'));
    } catch (error) {
      notify(
        error?.message ||
          t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_SAVE_FAQ_LEARNING_ERROR')
      );
    } finally {
      isSavingFaqLearning.value = false;
    }
  };

  const saveHandoff = async () => {
    const targetAssistantId = unref(assistantId);
    isSavingHandoff.value = true;
    try {
      const saved = await saveAssistantSection({
        successMessage: t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_HANDOFF_UPDATED'),
        config: buildHandoffConfig(),
        targetAssistantId,
      });
      if (!saved) return;
      markSaved(['handoff']);
      track(ACCOUNT_EVENTS.CAPTAIN_AGENT_HANDOFF_SAVED, {
        assistantId: targetAssistantId,
        hasBusinessHoursMessage: Boolean(
          identityForm.businessHoursHandoffMessage
        ),
        hasAfterHoursMessage: Boolean(identityForm.afterHoursHandoffMessage),
      });
    } catch (error) {
      notify(
        error?.message ||
          t('CAPTAIN_EXPERIENCE.SCRIPT.ALERT_SAVE_HANDOFF_ERROR')
      );
    } finally {
      isSavingHandoff.value = false;
    }
  };

  return {
    showDeleteAssistantModal,
    isDuplicatingAssistant,
    isDeletingAssistant,
    isSavingIdentity,
    isSavingBehavior,
    isSavingKnowledge,
    isSavingFaqLearning,
    isSavingHandoff,
    selectAssistant,
    handleAssistantCreated,
    duplicateAssistant,
    requestDeleteAssistant,
    deleteAssistant,
    saveAssistantSection,
    saveIdentity,
    saveBehavior,
    saveKnowledgeSettings,
    saveFaqLearningSettings,
    saveHandoff,
  };
}
